import { LEAD_STATUS_LABELS, type LeadStatus, PLAN_CODES, PLANS } from "@byfluent/domain";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import {
  Alert,
  Badge,
  type BadgeColor,
  Button,
  Chips,
  Empty,
  Loading,
  Modal,
  PageHeader,
  SelectInput,
  TextArea,
  TextInput,
} from "../../components/ui";
import { api, errorMessage, qs } from "../../lib/api";
import { ago, formatBrPhone } from "../../lib/format";
import type { Lead, Page } from "../../lib/types";

export const STATUS_COLOR: Record<LeadStatus, BadgeColor> = {
  new: "blue",
  in_conversation: "violet",
  link_sent: "gold",
  won: "green",
  lost: "red",
  no_reply: "gray",
};

const FILTERS: { value: LeadStatus | "all"; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "new", label: "Novos" },
  { value: "in_conversation", label: "Em conversa" },
  { value: "link_sent", label: "Link enviado" },
  { value: "won", label: "Ganhos" },
  { value: "lost", label: "Perdidos" },
  { value: "no_reply", label: "Sem resposta" },
];

function LeadRow({ lead, highlight }: { lead: Lead; highlight?: boolean }) {
  return (
    <Link to={`/vendas/leads/${lead.id}`} className={`list-item${highlight ? " highlight" : ""}`}>
      <div className="stack" style={{ gap: 2 }}>
        <div className="row" style={{ gap: 8 }}>
          <span className="code-tag">{lead.code}</span>
          <strong>{lead.name ?? "Sem nome ainda"}</strong>
        </div>
        <span className="small muted">
          {lead.phone ? `${formatBrPhone(lead.phone)} · ` : ""}
          {lead.planCode ? PLANS[lead.planCode].name : "Plano não informado"}
          {lead.utmCampaign ? ` · ${lead.utmCampaign}` : ""} · {ago(lead.createdAt)}
        </span>
      </div>
      <Badge color={STATUS_COLOR[lead.status]}>{LEAD_STATUS_LABELS[lead.status]}</Badge>
    </Link>
  );
}

export function Leads() {
  const [status, setStatus] = useState<LeadStatus | "all">("all");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const query = useInfiniteQuery({
    queryKey: ["leads", status, search],
    queryFn: ({ pageParam }) =>
      api.get<Page<Lead> & { stale: Lead[] }>(
        `/v1/leads${qs({ status: status === "all" ? undefined : status, q: search || undefined, cursor: pageParam ?? undefined, limit: 30 })}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 30_000,
  });
  const stale = query.data?.pages[0]?.stale ?? [];
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <PageHeader
        title="Leads"
        subtitle="Encontre o lead pelo código da mensagem do WhatsApp."
        actions={<Button onClick={() => setCreating(true)}>Cadastrar lead</Button>}
      />
      <form
        className="row"
        style={{ marginBottom: 12 }}
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(q.trim());
        }}
      >
        <input
          className="input"
          style={{ flex: 1, minWidth: 200 }}
          placeholder="Buscar por código, nome ou telefone"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Buscar"
        />
        <Button type="submit" variant="secondary">
          Buscar
        </Button>
      </form>
      <div style={{ marginBottom: 16 }}>
        <Chips value={status} onChange={setStatus} options={FILTERS} />
      </div>
      {query.isLoading ? <Loading /> : null}
      {query.error ? <Alert kind="risk">{errorMessage(query.error)}</Alert> : null}
      {stale.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <Alert kind="warning" title={`${stale.length} lead(s) novo(s) há mais de 24 h`}>
            Responda primeiro estes contatos.
          </Alert>
          <div style={{ marginTop: 8 }}>
            {stale.map((l) => (
              <LeadRow key={l.id} lead={l} highlight />
            ))}
          </div>
        </div>
      ) : null}
      {items.length === 0 && !query.isLoading ? <Empty>Nenhum lead por aqui.</Empty> : null}
      <div>
        {items.map((l) => (
          <LeadRow key={l.id} lead={l} />
        ))}
      </div>
      {query.hasNextPage ? (
        <div className="center" style={{ padding: 16 }}>
          <Button
            variant="secondary"
            loading={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            Carregar mais
          </Button>
        </div>
      ) : null}
      {creating ? <CreateLeadModal onClose={() => setCreating(false)} /> : null}
    </>
  );
}

function CreateLeadModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [planCode, setPlanCode] = useState<string>("IND-1H");
  const [notes, setNotes] = useState("");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const m = useMutation({
    mutationFn: () =>
      api.post<Lead>("/v1/leads", { name, phone, planCode, notes: notes || undefined }),
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      navigate(`/vendas/leads/${lead.id}`);
    },
  });
  return (
    <Modal title="Cadastrar lead" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        {m.error ? <Alert kind="risk">{errorMessage(m.error)}</Alert> : null}
        <TextInput label="Nome" required value={name} onChange={(e) => setName(e.target.value)} />
        <TextInput
          label="WhatsApp"
          inputMode="tel"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          hint="Com DDD"
        />
        <SelectInput
          label="Plano de interesse"
          value={planCode}
          onChange={(e) => setPlanCode(e.target.value)}
          options={PLAN_CODES.map((c) => ({ value: c, label: PLANS[c].name }))}
        />
        <TextArea label="Observações" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <Button type="submit" block loading={m.isPending}>
          Salvar lead
        </Button>
      </form>
    </Modal>
  );
}
